import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  LEAD_RATING_DEFS,
  LEAD_RATING_MAP,
  LEAD_STATUS_DEFS,
  LEAD_STATUS_MAP,
  type LeadRating,
  type LeadStatus,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { Menu, type MenuAction } from '../../components/Menu';
import { Modal } from '../../components/ui';
import { OwnerPickerModal } from '../../components/People';
import { useToast } from '../../components/Toast';
import { DeleteDialog } from '../../components/DeleteDialog';
import { Icon } from '../../components/Icon';
import { ActivityComposer } from './ActivityComposer';

/**
 * الإجراءات السريعة على عميل محتمل.
 *
 * مكوّن واحد لصفّ القائمة ولصفحة التفصيل: القاعدة التي تمنع إجراءً — كتغيير
 * حالة عميل محوَّل — تُكتب مرة فلا تفترق بين موضعين.
 *
 * وما لم يُبنَ بعد ليس هنا. لا زرّ «فتح المحادثة» ولا «إرسال رسالة»: قنوات
 * ميتا لم تُربط، وزرٌّ يفتح نافذة فارغة أسوأ من غيابه — يُعلّم المستخدم أن
 * أزرار النظام قد لا تعني شيئًا.
 */

type Mode = 'owner' | 'status' | 'rating' | 'activity' | 'note' | 'delete' | null;

export interface QuickLead {
  id: string;
  name: string;
  status: LeadStatus;
  rating: LeadRating;
  ownerId?: string | null;
  email?: string | null;
  phone?: string | null;
  convertedAt?: string | null;
}

export function LeadQuickActions({
  lead,
  onChanged,
  className = 'btn btn-sm btn-icon btn-ghost',
  showOpen = true,
}: {
  lead: QuickLead;
  onChanged: () => void;
  className?: string;
  /** زر «فتح الملف» يُخفى داخل الملف نفسه. */
  showOpen?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { atLeast } = useAuth();
  const toast = useToast();
  const [mode, setMode] = useState<Mode>(null);

  const converted = !!lead.convertedAt || lead.status === 'CONVERTED';

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/crm/leads/${lead.id}`, { method: 'PATCH', body }),
    /*
     * التأكيد يذكر ما تغيّر لا «تم الحفظ».
     *
     * النافذة تُغلق والجدول يعود كما كان في نظر العين، فرسالةٌ عامة لا تقول
     * إن كان ما وقع هو ما قُصد. و`variables` هي جسم الطلب نفسه، فمنه تُشتق.
     */
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries();
      setMode(null);
      toast(changeMessage(lead.name, variables));
      onChanged();
    },
  });

  const actions: MenuAction[] = [
    {
      key: 'owner',
      label: 'تغيير المسؤول',
      icon: 'users',
      onSelect: () => setMode('owner'),
    },
    {
      key: 'call',
      label: 'اتصال',
      icon: 'phone',
      disabled: !lead.phone,
      title: lead.phone ?? 'لا رقم مسجَّل',
      /*
       * `tel:` يسلّم الرقم إلى ما يتولّى المكالمات على الجهاز.
       * ليس تسجيلًا للمكالمة — التسجيل إجراء مستقل بعدها.
       */
      onSelect: () => {
        navigate(`/callcenter?dial=${encodeURIComponent(lead.phone ?? '')}`);
      },
    },
    {
      key: 'mail',
      label: 'إرسال بريد',
      icon: 'mail',
      disabled: !lead.email,
      title: lead.email ?? 'لا بريد مسجَّل',
      onSelect: () => {
        window.location.href = `mailto:${lead.email}`;
      },
    },
    {
      key: 'note',
      label: 'إضافة ملاحظة',
      icon: 'note',
      separated: true,
      onSelect: () => setMode('note'),
    },
    {
      key: 'activity',
      label: 'تسجيل نشاط',
      icon: 'clipboard',
      onSelect: () => setMode('activity'),
    },
    {
      key: 'status',
      label: 'تغيير الحالة',
      icon: 'refresh',
      disabled: converted,
      title: converted ? 'المحوَّل لا تُعدَّل حالته بعد التحويل.' : undefined,
      onSelect: () => setMode('status'),
    },
    {
      key: 'rating',
      label: 'تغيير الأولوية',
      icon: 'zap',
      onSelect: () => setMode('rating'),
    },
    {
      /*
       * «استبعاد» بديل الأرشفة.
       *
       * لا حقل أرشيف في العميل المحتمل، ولا حاجة إليه: حالة «مستبعد» نهائية
       * تُخرجه من قوائم المتابعة ومن كل دلوٍ يشترط «مفتوح»، ويبقى في السجل
       * وفي التقارير. وهو ما تعنيه الأرشفة عمليًا.
       */
      key: 'archive',
      label: 'استبعاد',
      icon: 'x',
      disabled: converted || lead.status === 'JUNK',
      title: converted
        ? 'المحوَّل لا تُعدَّل حالته بعد التحويل.'
        : lead.status === 'JUNK'
          ? 'مستبعد بالفعل.'
          : 'يخرج من قوائم المتابعة ويبقى في السجل.',
      onSelect: () => patch.mutate({ status: 'JUNK' }),
    },
  ];

  if (showOpen) {
    actions.push({
      key: 'open',
      label: 'فتح الملف',
      icon: 'arrowLeft',
      separated: true,
      onSelect: () => navigate(`/crm/leads/${lead.id}`),
    });
  }

  if (atLeast('MANAGER')) {
    actions.push({
      key: 'delete',
      label: 'حذف',
      icon: 'trash',
      danger: true,
      separated: !showOpen,
      onSelect: () => setMode('delete'),
    });
  }

  return (
    <>
      <Menu actions={actions} label={`إجراءات ${lead.name}`} className={className} />

      {mode === 'owner' && (
        <OwnerPickerModal
          currentId={lead.ownerId ?? null}
          title={`مسؤول ${lead.name}`}
          pending={patch.isPending}
          error={patch.error}
          onPick={(userId) => patch.mutate({ ownerId: userId })}
          onClose={() => setMode(null)}
        />
      )}

      {mode === 'status' && (
        <ChoiceModal
          title={`حالة ${lead.name}`}
          options={LEAD_STATUS_DEFS.filter((s) => s.key !== 'CONVERTED').map((s) => ({
            key: s.key,
            label: s.label,
          }))}
          current={lead.status}
          pending={patch.isPending}
          error={patch.error}
          note={
            /* التحويل ليس حالة تُختار: له مسار خاص يُنشئ حسابًا وصفقة. */
            'التحويل إلى حساب يتم من داخل ملف العميل، لا بتغيير الحالة.'
          }
          onPick={(key) => patch.mutate({ status: key })}
          onClose={() => setMode(null)}
        />
      )}

      {mode === 'rating' && (
        <ChoiceModal
          title={`تصنيف ${lead.name}`}
          options={LEAD_RATING_DEFS.map((r) => ({ key: r.key, label: r.label }))}
          current={lead.rating}
          pending={patch.isPending}
          error={patch.error}
          onPick={(key) => patch.mutate({ rating: key })}
          onClose={() => setMode(null)}
        />
      )}

      {mode === 'activity' && (
        <Modal title={`نشاط على ${lead.name}`} onClose={() => setMode(null)}>
          <ActivityComposer
            link={{ leadId: lead.id }}
            onDone={() => {
              void queryClient.invalidateQueries();
              setMode(null);
              onChanged();
            }}
          />
        </Modal>
      )}

      {mode === 'note' && (
        <Modal title={`ملاحظة على ${lead.name}`} onClose={() => setMode(null)}>
          <ActivityComposer
            link={{ leadId: lead.id }}
            defaultType="NOTE"
            startOpen
            onDone={() => {
              void queryClient.invalidateQueries();
              setMode(null);
              toast(`أُضيفت ملاحظة على «${lead.name}»`);
              onChanged();
            }}
          />
        </Modal>
      )}

      {mode === 'delete' && (
        <DeleteDialog
          entity="lead"
          id={lead.id}
          endpoint={`/crm/leads/${lead.id}`}
          onClose={() => setMode(null)}
          onDeleted={() => {
            setMode(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}

/* ==========================================================================
   نوافذ الاختيار
   ========================================================================== */

/** اختيار قيمة من قائمة قصيرة — الحالة والتصنيف. */
function ChoiceModal({
  title,
  options,
  current,
  pending,
  error,
  note,
  onPick,
  onClose,
}: {
  title: string;
  options: Array<{ key: string; label: string }>;
  current: string;
  pending: boolean;
  error: unknown;
  note?: string;
  onPick: (key: string) => void;
  onClose: () => void;
}) {
  return (
    <Modal title={title} onClose={onClose}>
      {error != null && (
        <div className="alert alert-danger mb-4">
          {error instanceof Error ? error.message : 'تعذّر التغيير.'}
        </div>
      )}

      <div className="pick-list">
        {options.map((o) => (
          <button
            key={o.key}
            type="button"
            className={`pick-row${o.key === current ? ' pick-row-current' : ''}`}
            disabled={pending || o.key === current}
            onClick={() => onPick(o.key)}
          >
            <span className="pick-row-text">
              <strong>{o.label}</strong>
              {o.key === current && <small>الحالة الحالية</small>}
            </span>
            {o.key === current && <Icon name="check" size={16} />}
          </button>
        ))}
      </div>

      {note && <div className="alert alert-info mt-4">{note}</div>}
    </Modal>
  );
}

/**
 * صياغة التأكيد من جسم الطلب.
 *
 * تُقرأ الحقول المعروفة وحدها: تغييرٌ لم يُصَغ له نصّ يُؤكَّد بجملة عامة بدل
 * أن يمرّ بلا أثر — والصمت بعد إجراء يجعل المستخدم يعيده.
 */
function changeMessage(name: string, body: Record<string, unknown>): string {
  if ('ownerId' in body) {
    return body.ownerId ? `نُقل «${name}» إلى مسؤول جديد` : `أُلغي إسناد «${name}»`;
  }
  if ('status' in body) {
    const label = LEAD_STATUS_MAP[body.status as LeadStatus]?.label ?? String(body.status);
    return `صارت حالة «${name}»: ${label}`;
  }
  if ('rating' in body) {
    const label = LEAD_RATING_MAP[body.rating as LeadRating]?.label ?? String(body.rating);
    return `صار تصنيف «${name}»: ${label}`;
  }
  return `حُفظ التغيير على «${name}»`;
}
